import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedCountdown = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const bg = searchParams.get('bg') || 'transparent';
  const textColor = searchParams.get('color') || '#ffffff';
  const labelColor = searchParams.get('labelColor') || '#a5b4fc';
  const size = searchParams.get('size') || 'large'; // small, medium, large, xlarge
  const showLabel = searchParams.get('label') !== 'false';
  const showProgress = searchParams.get('progress') !== 'false';
  const showNext = searchParams.get('next') !== 'false';

  const [countdown, setCountdown] = useState({
    active: false,
    type: null,
    secondsRemaining: 0,
    totalSeconds: 0,
    label: 'Waiting...',
    endTime: null,
    showSoon: false,
    showInProgress: false  // For phases like orientation that are "in progress" without countdown
  });

  const [partyPhase, setPartyPhase] = useState('not_started');
  const [nextPhase, setNextPhase] = useState('');
  const [marketState, setMarketState] = useState({ isOpen: false });
  const [tradingBlock, setTradingBlock] = useState(1);
  const [currentMacroGame, setCurrentMacroGame] = useState(null);
  const socketRef = useRef(null);

  const sizeConfig = {
    small: { time: '32px', label: '10px', next: '12px' },
    medium: { time: '48px', label: '12px', next: '14px' },
    large: { time: '72px', label: '14px', next: '16px' },
    xlarge: { time: '120px', label: '18px', next: '20px' }
  };

  const currentSize = sizeConfig[size] || sizeConfig.large;

  // Format seconds to MM:SS or H:MM:SS
  const formatCountdown = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Trading session names by block (themed after global markets)
  const getTradingSessionName = (block) => {
    const sessions = {
      1: 'Asian Session',
      2: 'European Session',
      3: 'Canadian Session',
      4: 'Power Hour'  // 5-min final trading before closing bell
    };
    return sessions[block] || 'Trading';
  };

  // Helper to get phase label
  const getPhaseLabel = (phase, block = 1, macroGameName = null) => {
    const labels = {
      'not_started': 'Ready to Start',
      'registration': 'Registration Open',
      'premarket': 'Pre-Market',
      'orientation': 'Orientation',
      'trading': getTradingSessionName(block),
      'macro_game': macroGameName || getGameName(block) || 'Macro Game',
      'power_hour': 'Power Hour',
      'closing': 'Closing Bell',
      'liquidity_crisis': 'Liquidity Crisis',
      'results': 'Results',
      'winners': 'Winners',
      'ended': 'Party Ended'
    };
    return labels[phase] || phase;
  };

  // Game names by trading block (macro games after each trading session)
  const getGameName = (block) => {
    const games = {
      1: 'Portfolio Warfare',
      2: 'Dead Cat Bounce',
      3: 'A Brief Case'
      // Block 4 has no macro game - goes straight to closing bell
    };
    return games[block] || 'Macro Game';
  };

  // Helper to get next phase
  const getNextPhaseLabel = (phase, block = 1) => {
    const nextMap = {
      'not_started': 'Registration + Premarket',
      'registration': 'Orientation',
      'premarket': 'Orientation',
      'orientation': 'Opening Bell',
      'trading': block >= 4 ? 'Closing Bell' : getGameName(block),
      'macro_game': block >= 3 ? 'Power Hour' : getTradingSessionName(block + 1),
      'power_hour': 'Closing Bell',
      'closing': 'Winners / Liquidity Crisis',
      'liquidity_crisis': 'Winners',
      'winners': 'Party Complete!',
      'results': 'Party Complete!',
      'ended': 'Party Complete!'
    };
    return nextMap[phase] || '';
  };

  // Fetch initial state
  useEffect(() => {
    const fetchState = async () => {
      try {
        let hasActiveCountdown = false;
        let currentPhase = 'not_started';
        let currentBlock = 1;
        let currentMacroGameName = null;

        // These phases are "in progress" - they show ACTIVE, not a countdown
        const inProgressPhases = ['orientation', 'macro_game', 'power_hour', 'liquidity_crisis', 'winners', 'results'];
        // These phases are "waiting" for the next transition
        const waitingPhases = ['premarket', 'closing'];

        // FIRST: Get party state to know current phase and all countdowns
        const partyRes = await fetch(`${API_BASE_URL}/api/party-state`);
        let partyStateData = null;
        if (partyRes.ok) {
          partyStateData = await partyRes.json();
          if (partyStateData.partyState) {
            currentPhase = partyStateData.partyState.phase || 'not_started';
            currentBlock = partyStateData.partyState.tradingBlockNumber || 1;
            currentMacroGameName = partyStateData.partyState.currentMacroGame || null;
            setPartyPhase(currentPhase);
            setTradingBlock(currentBlock);
            setCurrentMacroGame(currentMacroGameName);
            setNextPhase(getNextPhaseLabel(currentPhase, currentBlock));
          }
          if (partyStateData.marketState) {
            setMarketState(partyStateData.marketState);
          }
        }

        // Check for trading block countdown from party-state (priority for trading phase)
        if (!hasActiveCountdown && partyStateData?.countdowns?.tradingBlock?.active) {
          const tb = partyStateData.countdowns.tradingBlock;
          hasActiveCountdown = true;
          setTradingBlock(tb.blockNumber || currentBlock);
          setCountdown({
            active: true,
            type: 'trading-block',
            secondsRemaining: tb.secondsRemaining,
            totalSeconds: tb.totalSeconds,
            label: getTradingSessionName(tb.blockNumber || currentBlock),
            endTime: tb.endTime || (Date.now() + (tb.secondsRemaining * 1000)),
            showSoon: false,
            showInProgress: false
          });
          setNextPhase(tb.gameName || getNextPhaseLabel(currentPhase, currentBlock));
        }

        // If we're in an "in progress" phase (but NOT trading), show ACTIVE
        if (!hasActiveCountdown && inProgressPhases.includes(currentPhase)) {
          setCountdown({
            active: false,
            type: currentPhase,
            secondsRemaining: 0,
            totalSeconds: 0,
            label: getPhaseLabel(currentPhase, currentBlock, currentMacroGameName),
            endTime: null,
            showSoon: false,
            showInProgress: true
          });
          hasActiveCountdown = true; // Skip other countdown fetches
        }

        // Check for market open countdown from party-state
        if (!hasActiveCountdown && partyStateData?.countdowns?.marketOpen?.active) {
          const mo = partyStateData.countdowns.marketOpen;
          hasActiveCountdown = true;
          setCountdown({
            active: true,
            type: 'market-open',
            secondsRemaining: mo.secondsRemaining,
            totalSeconds: mo.totalSeconds,
            label: 'Opening Bell',
            endTime: Date.now() + (mo.secondsRemaining * 1000)
          });
        }

        // Check for macro game countdown from party-state
        if (!hasActiveCountdown && partyStateData?.countdowns?.macroGame?.active) {
          const mg = partyStateData.countdowns.macroGame;
          hasActiveCountdown = true;
          setCountdown({
            active: true,
            type: 'macro-game',
            secondsRemaining: mg.secondsRemaining,
            totalSeconds: mg.totalSeconds,
            label: mg.gameName || 'Macro Game',
            endTime: Date.now() + (mg.secondsRemaining * 1000)
          });
        }

        // Check for premarket countdown from party-state
        if (!hasActiveCountdown && partyStateData?.countdowns?.premarket?.active) {
          const pm = partyStateData.countdowns.premarket;
          if (pm.secondsRemaining > 0) {
            hasActiveCountdown = true;
            setCountdown({
              active: true,
              type: 'premarket',
              secondsRemaining: pm.secondsRemaining,
              totalSeconds: pm.totalSeconds,
              label: 'Pre-Market',
              endTime: Date.now() + (pm.secondsRemaining * 1000)
            });
            setNextPhase('Orientation');
          }
        }

        // If still no countdown, show waiting state
        if (!hasActiveCountdown) {
          setCountdown(prev => ({
            ...prev,
            active: false,
            type: currentPhase,
            label: getPhaseLabel(currentPhase, currentBlock, currentMacroGameName),
            showInProgress: false,
            showSoon: waitingPhases.includes(currentPhase)
          }));
        }
      } catch (error) {
        console.error('Error fetching state:', error);
      }
    };

    fetchState();
  }, []);

  // Set up socket for real-time updates
  useEffect(() => {
    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    // HARD countdowns: auto-trigger when done (market-open, market-close)
    socketRef.current.on('countdown-start', (data) => {
      setCountdown({
        active: true,
        type: data.countdownType || 'market-open',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.countdownType === 'market-open' ? 'Opening Bell' :
               data.countdownType === 'market-close' ? 'Closing Bell' :
               data.countdownType === 'macro-game' ? data.gameName || 'Macro Game' : 'Countdown',
        endTime: data.endTime,
        showSoon: false
      });
    });

    socketRef.current.on('countdown-update', (data) => {
      setCountdown(prev => ({
        ...prev,
        secondsRemaining: data.seconds,
        endTime: data.endTime
      }));
    });

    socketRef.current.on('countdown-cancelled', () => {
      setCountdown({
        active: false,
        type: null,
        secondsRemaining: 0,
        totalSeconds: 0,
        label: 'Countdown Cancelled',
        endTime: null
      });
    });

    // HARD countdown: macro game (auto-pauses trading when done)
    socketRef.current.on('macro-game-countdown', (data) => {
      setCountdown({
        active: true,
        type: 'macro-game',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.gameName || 'Macro Game',
        endTime: data.endTime,
        showSoon: false
      });
    });

    socketRef.current.on('macro-game-countdown-end', () => {
      setCountdown(prev => ({
        ...prev,
        active: false,
        secondsRemaining: 0,
        label: prev.label || 'Game Starting',
        showSoon: false,
        showInProgress: true  // Game is now active
      }));
    });

    // SOFT countdown: trading session (visual guide only, admin controls transitions)
    socketRef.current.on('trading-block-countdown-start', (data) => {
      setTradingBlock(data.blockNumber);
      setCountdown({
        active: true,
        type: 'trading-block',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.sessionName || getTradingSessionName(data.blockNumber),
        endTime: data.endTime,
        showSoon: false,
        showInProgress: false
      });
      setNextPhase(data.nextGame ? data.nextGame : 'Macro Game');
    });

    // Trading block countdown cleared (macro game started)
    socketRef.current.on('trading-block-countdown-clear', () => {
      setCountdown(prev => ({
        ...prev,
        active: false,
        type: null,
        secondsRemaining: 0
      }));
    });

    // HARD countdown: Closing Bell (auto-closes market when done)
    socketRef.current.on('close-countdown-start', (data) => {
      setCountdown({
        active: true,
        type: 'market-close',  // HARD countdown type - auto-closes when done
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: 'Closing Bell',
        endTime: data.endTime || (Date.now() + data.seconds * 1000),
        showSoon: false,
        showInProgress: false
      });
      setNextPhase('Winners');
    });

    // Close countdown tick updates
    socketRef.current.on('close-countdown-tick', (data) => {
      setCountdown(prev => ({
        ...prev,
        secondsRemaining: data.seconds,
        endTime: data.endTime
      }));
    });

    // Handle registration start (resets countdown state)
    socketRef.current.on('registration-start', () => {
      setPartyPhase('registration');
      setCountdown({
        active: false,
        type: null,
        secondsRemaining: 0,
        totalSeconds: 0,
        label: 'Registration Open',
        endTime: null,
        showSoon: false
      });
      setNextPhase('Orientation');
    });

    // Handle party restart/reset
    socketRef.current.on('party-restart', () => {
      setPartyPhase('not_started');
      setCountdown({
        active: false,
        type: null,
        secondsRemaining: 0,
        totalSeconds: 0,
        label: 'Ready to Start',
        endTime: null,
        showSoon: false
      });
      setNextPhase('Registration + Premarket');
      setMarketState({ isOpen: false });
      setTradingBlock(1);
    });

    socketRef.current.on('market-bell', (data) => {
      if (data.type === 'OPENING_BELL') {
        setMarketState({ isOpen: true });
        setCountdown(prev => ({ ...prev, active: false }));
      } else if (data.type === 'CLOSING_BELL') {
        setMarketState({ isOpen: false });
        setCountdown(prev => ({ ...prev, active: false }));
      }
    });

    socketRef.current.on('party-state-update', (data) => {
      if (data.partyState) {
        const phase = data.partyState.phase || 'not_started';
        const block = data.partyState.tradingBlockNumber || 1;
        const macroGame = data.partyState.currentMacroGame || null;
        setPartyPhase(phase);
        setTradingBlock(block);
        setCurrentMacroGame(macroGame);
        setNextPhase(getNextPhaseLabel(phase, block));

        // Update display state if no active countdown
        setCountdown(prev => {
          if (!prev.active) {
            const inProgressPhases = ['orientation', 'macro_game', 'power_hour', 'liquidity_crisis', 'winners', 'results'];
            const waitingPhases = ['premarket', 'closing'];
            return {
              ...prev,
              type: phase,
              label: getPhaseLabel(phase, block, macroGame),
              showInProgress: inProgressPhases.includes(phase),
              showSoon: waitingPhases.includes(phase)
            };
          }
          return prev;
        });
      }
      if (data.marketState) {
        setMarketState(data.marketState);
      }
    });

    // Premarket events
    socketRef.current.on('premarket-start', (data) => {
      setPartyPhase('premarket');
      const totalSeconds = data.minutes ? data.minutes * 60 : Math.floor(data.duration / 1000);
      setCountdown({
        active: true,
        type: 'premarket',
        secondsRemaining: totalSeconds,
        totalSeconds: totalSeconds,
        label: 'Pre-Market',  // Current phase is Pre-Market
        endTime: data.endTime,
        showSoon: false
      });
      setNextPhase('Orientation');  // Next is Orientation, then Opening Bell
    });

    socketRef.current.on('premarket-end', () => {
      setCountdown(prev => ({
        ...prev,
        active: false,
        label: 'Market Opening Soon'
      }));
    });

    // Handle trading paused/resumed
    socketRef.current.on('trading-paused', () => {
      setCountdown(prev => {
        if (!prev.active) {
          return { ...prev, label: 'Trading Paused' };
        }
        return prev;
      });
    });

    socketRef.current.on('trading-resumed', (data) => {
      const block = data?.tradingBlockNumber || data?.blockNumber || 1;
      setTradingBlock(block);
      setCountdown(prev => {
        if (!prev.active) {
          return { ...prev, label: getTradingSessionName(block) };
        }
        return prev;
      });
    });

    // Handle orientation events
    socketRef.current.on('orientation-start', () => {
      setPartyPhase('orientation');
      setCountdown({
        active: false,
        type: 'orientation',
        secondsRemaining: 0,
        totalSeconds: 0,
        label: 'Orientation',
        endTime: null,
        showSoon: false,
        showInProgress: true  // New flag for phases that are "in progress" without countdown
      });
      setNextPhase('Opening Bell');
    });

    socketRef.current.on('orientation-end', () => {
      setCountdown(prev => ({
        ...prev,
        label: 'Get Ready',
        showInProgress: false,
        showSoon: true
      }));
      setNextPhase('Market Open');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Countdown tick effect
  useEffect(() => {
    if (!countdown.active || !countdown.endTime) return;

    const tick = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((countdown.endTime - now) / 1000));

      if (remaining <= 0) {
        // HARD countdowns auto-transition when done:
        // - market-open: auto-opens market + starts Phase 1
        // - market-close: auto-closes market
        // - macro-game: auto-pauses trading for game
        // SOFT countdowns (trading-block, premarket) show "SOON" until manual transition
        const hardCountdownTypes = ['market-open', 'market-close', 'macro-game'];
        const isHardCountdown = hardCountdownTypes.includes(countdown.type);

        setCountdown(prev => ({
          ...prev,
          active: false,
          secondsRemaining: 0,
          // Show "SOON" for soft countdowns, keep label for hard ones
          showSoon: !isHardCountdown
        }));
      } else {
        setCountdown(prev => ({
          ...prev,
          secondsRemaining: remaining
        }));
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [countdown.active, countdown.endTime, countdown.type]);

  // Get color based on countdown type
  const getColor = () => {
    if (!countdown.active) return '#64748b';
    switch (countdown.type) {
      case 'market-open': return '#10b981';   // Green
      case 'market-close': return '#ef4444'; // Red
      case 'macro-game': return '#8b5cf6';   // Purple
      case 'trading-block': return '#3b82f6'; // Blue
      case 'orientation': return '#a855f7';   // Violet
      case 'power-hour': return '#f97316';   // Orange - urgent/exciting
      default: return '#3b82f6';
    }
  };

  return (
    <div
      className="embed-countdown"
      style={{
        background: bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100%',
        height: '100%',
        position: 'relative',
        padding: '20px',
        boxSizing: 'border-box',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      {/* fullscreen button removed — not needed when embedded in monitor */}
      {/* Label */}
      {showLabel && (
        <div style={{
          fontSize: currentSize.label,
          color: labelColor,
          textTransform: 'uppercase',
          letterSpacing: '3px',
          marginBottom: '10px'
        }}>
          {countdown.active ? countdown.label : countdown.label || getPhaseLabel(partyPhase, tradingBlock)}
        </div>
      )}

      {/* Countdown Time */}
      <div style={{
        fontSize: (countdown.showSoon || countdown.showInProgress || (!countdown.active && partyPhase === 'trading'))
          ? `calc(${currentSize.time} * 0.5)` : currentSize.time,
        fontWeight: 'bold',
        fontFamily: (countdown.showSoon || countdown.showInProgress || (!countdown.active && partyPhase === 'trading'))
          ? 'system-ui, -apple-system, sans-serif' : 'monospace',
        color: countdown.active ? textColor :
               countdown.showInProgress ? '#8b5cf6' :  // Purple for in-progress phases
               (!countdown.active && partyPhase === 'trading') ? '#22c55e' :  // Green for active trading
               countdown.showSoon ? '#fbbf24' : '#64748b',
        textShadow: countdown.active ? `0 0 30px ${getColor()}40` :
                    countdown.showInProgress ? '0 0 20px #8b5cf640' :
                    (!countdown.active && partyPhase === 'trading') ? '0 0 20px #22c55e40' :
                    countdown.showSoon ? '0 0 20px #fbbf2440' : 'none',
        lineHeight: 1,
        animation: (countdown.showSoon || countdown.showInProgress || (!countdown.active && partyPhase === 'trading'))
          ? 'pulse 2s ease-in-out infinite' : 'none'
      }}>
        {countdown.active ? formatCountdown(countdown.secondsRemaining) :
         countdown.showInProgress ? 'ACTIVE' :
         (!countdown.active && partyPhase === 'trading') ? 'ACTIVE' :
         countdown.showSoon ? 'SOON' : '--:--'}
      </div>

      {/* Pulse animation for SOON and IN PROGRESS */}
      {(countdown.showSoon || countdown.showInProgress) && (
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
          }
        `}</style>
      )}

      {/* Progress Bar */}
      {showProgress && countdown.active && countdown.totalSeconds > 0 && (
        <div style={{
          width: '80%',
          maxWidth: '400px',
          height: '8px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '4px',
          marginTop: '20px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${(countdown.secondsRemaining / countdown.totalSeconds) * 100}%`,
            background: getColor(),
            borderRadius: '4px',
            transition: 'width 1s linear'
          }} />
        </div>
      )}

      {/* What's Next */}
      {showNext && nextPhase && (
        <div style={{
          marginTop: '20px',
          fontSize: currentSize.next,
          color: '#94a3b8'
        }}>
          Next: <span style={{ color: '#f1f5f9', fontWeight: 'bold' }}>{nextPhase}</span>
        </div>
      )}
    </div>
  );
};

export default EmbedCountdown;
